import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { formatMinutesAsTime, parseTimeToMinutes } from '../../utils/time';
import { useEffect, useMemo, useState, useRef } from 'react';
import { LuLock } from 'react-icons/lu';
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CardSetupForm from '../../Components/Stripe/CardSetupForm';
import { bookAppointment, confirmCardSetup, getPaymentConfig, lookupCustomer, getQuestionnaire, previewDiscount } from '../../services/OnlineStore';
import { formatPrice } from '../../utils/pricing';
import { requiredDeposit } from '../../utils/deposit';
import { normalizeBookingServices } from '../../utils/serviceBuilder';
import Page  from '../../Components/Page/BookingPage';
import CheckoutSummary from '../../Components/Book/CheckoutSummary/CheckoutSummary';
import LoadingScreen from '../../Components/LoadingScreen/LoadingScreen';
import styles from './checkout.module.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_placeholder");
const domain = process.env.REACT_APP_DOMAIN || "http://localhost:3000";
const checkoutFormId = 'checkout-payment-form';

// Stripe renders in an iframe, so theme tokens must be resolved to literal values.
function getStripeAppearance() {
    const root = getComputedStyle(document.documentElement);
    const token = (name, fallback) => root.getPropertyValue(name).trim() || fallback;

    return {
        theme: 'stripe',
        variables: {
            colorPrimary: token('--action-primary', '#b04e4e'),
            colorBackground: token('--background-50', '#f9ecec'),
            colorText: token('--text-900', '#270c0c'),
            borderRadius: token('--radius-sm', '8px'),
            fontFamily: 'Montserrat, system-ui, sans-serif',
        },
    };
}

export function getAttributeSelectionIds(attributeSelection = {}) {
    const selections = Object.values(attributeSelection)
        .filter((selection) => selection && typeof selection === 'object');

    return {
        attributeIDs: [...new Set(
            selections
                .map((selection) => Number(selection.attribute_id))
                .filter((id) => Number.isFinite(id) && id > 0)
        )],
        attributeValueIDs: [...new Set(
            selections
                .map((selection) => Number(selection.attribute_value_id))
                .filter((id) => Number.isFinite(id) && id > 0)
        )],
    };
}

export function buildAppointmentPayload(booking = {}, contactInfo = {}, endTime = '', questions = [], answers = {}) {
    const services = normalizeBookingServices(booking);
    const primaryService = services[0] ?? {};
    const { attributeIDs, attributeValueIDs } = getAttributeSelectionIds(
        primaryService.attribute_selection ?? booking.attribute_selection
    );
    const extraServiceCount = Math.max(0, services.length - 1);
    const title = primaryService.product_name ?? booking.product_name;
    const topLevelAppointmentAttributes = services.length > 0
        ? {}
        : (primaryService.attribute_selection ?? booking.attribute_selection ?? {});
    const questionnaireAnswers = Array.isArray(questions)
        ? questions
            .filter((question) => question && question.id !== undefined && question.id !== null)
            .filter((question) => answers[question.id] !== undefined && answers[question.id] !== null)
            .map((question) => ({
                question_id: Number(question.id),
                question: question.question ?? '',
                answer: String(answers[question.id]).trim(),
            }))
            .filter((entry) => entry.question_id > 0 && entry.question)
        : [];

    return {
        user_id: booking.user_id ?? booking.selectedStaffID ?? booking.staff_id,
        services: services.map((service, index) => ({
            product_id: service.product_id,
            sequence: index + 1,
            price: service.total_price,
            duration: service.total_duration,
            appointment_attributes: service.attribute_selection ?? {},
            attributes: service.attributes
                .filter((attribute) => attribute.attribute_id && attribute.attribute_value_id)
                .map((attribute) => ({
                    attribute_id: attribute.attribute_id,
                    attribute_value_id: attribute.attribute_value_id,
                    attribute_name: attribute.name,
                })),
        })),
        // Retained until the backend reads services[]; mirrors the first service.
        product_id: primaryService.product_id ?? booking.product_id,
        attribute_ids: attributeIDs,
        attribute_value_ids: attributeValueIDs,
        customer_phone: contactInfo.phoneNumber,
        customer_email: contactInfo.email,
        customer_first_name: contactInfo.firstName,
        customer_last_name: contactInfo.lastName,
        date: booking.date,
        start_time: booking.slotValue,
        end_time: endTime,
        appointment_title: extraServiceCount > 0
            ? `${title} + ${extraServiceCount} more`
            : `${title} Appointment`,
        appointment_attributes: topLevelAppointmentAttributes,
        questionnaire_answers: questionnaireAnswers,
    };
}

const CardPaymentForm = ({ onIntentCreated, onSuccess, onFailure, setProcessing, processing = false }) => {
    const verificationRef = useRef(null);

    // Errors thrown here surface through CardSetupForm's onError.
    const handleBeforeConfirm = async () => {
        verificationRef.current = null;
        const verification = await onIntentCreated();
        verificationRef.current = verification;
        return verification;
    };

    const handleSuccess = async ({ setupIntent }) => {
        const verification = verificationRef.current;

        if (setupIntent?.status === 'succeeded') {
            try {
                await confirmCardSetup(setupIntent.id);
            } catch (confirmErr) {
                console.error('Failed to store card on file:', confirmErr);
            }
        }

        onSuccess({
            setupIntent,
            verification,
        });
    };

    return (
        <CardSetupForm
            onBeforeConfirm={handleBeforeConfirm}
            onSuccess={handleSuccess}
            onError={(message) => {
                // Without a verification the booking was never created, so the customer can fix it in place.
                onFailure({ message, type: verificationRef.current ? 'payment' : 'booking' });
            }}
            showError={false}
            returnUrl={`${domain}/payment-success`}
            onSavingChange={setProcessing}
            disabled={processing}
            processing={processing}
            formId={checkoutFormId}
            showSubmitButton={false}
            className={styles.paymentForm}
        />
    );
};

export function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || '').trim());
}

export function isValidPhone(value) {
    const digits = String(value || '').replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
}

export function isReadyForLookup(contactInfo = {}) {
    return Boolean(
        contactInfo.firstName?.trim() &&
        contactInfo.lastName?.trim() &&
        isValidEmail(contactInfo.email) &&
        isValidPhone(contactInfo.phoneNumber)
    );
}

function CheckoutPage() {
    const { timeSlot } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const booking = location?.state ?? {};
    const duration = Number(booking.total_duration ?? booking?.productDuration?.duration ?? 0);
    const price = Number(booking.total_price ?? booking?.productDuration?.price ?? 0);
    const endTime = formatMinutesAsTime(parseTimeToMinutes(booking.slotValue) + duration);
    
    const [contactInfo, setContactInfo] = useState({ phoneNumber: '', firstName: '', lastName: '', email: '' });
    const [paymentError, setPaymentError] = useState('');
    const [processing, setProcessing] = useState(false);
    const [intentReady, setIntentReady] = useState(false);
    const [deposit, setDeposit] = useState(null);
    const [depositError, setDepositError] = useState('');
    const [discountCode, setDiscountCode] = useState('');
    const [discount, setDiscount] = useState(null);
    const [discountError, setDiscountError] = useState('');
    const [checkingDiscount, setCheckingDiscount] = useState(false);
    const discountRequestId = useRef(0);
    const bookingAttempt = useRef(null);
    const [bookingLocked, setBookingLocked] = useState(false);
    const discountAmount = discount
        ? Math.floor(price / 100) * discount.percentage + Math.floor(((price % 100) * discount.percentage + 50) / 100)
        : 0;
    const discountedPrice = price - discountAmount;
    const depositDue = deposit === null ? null : requiredDeposit(discountedPrice, deposit);
    const [agreed, setAgreed] = useState(false);
    const [isContactInfoComplete, setIsContactInfoComplete] = useState(false);
    const [existingAnswers, setExistingAnswers] = useState([]);
    const lastLookupKey = useRef('');
    const lookupRequestId = useRef(0);
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const touchedAnswers = useRef({});

    const clearDiscount = (code = '') => {
        discountRequestId.current += 1;
        setDiscountCode(code);
        setDiscount(null);
        setDiscountError('');
        setCheckingDiscount(false);
        setAgreed(false);
    };

    const applyDiscount = async () => {
        const code = discountCode.trim();
        const requestId = ++discountRequestId.current;
        setDiscountError('');
        setCheckingDiscount(true);
        setAgreed(false);
        try {
            const result = await previewDiscount(code);
            if (requestId !== discountRequestId.current) return;
            if (!Number.isInteger(result?.percentage) || result.percentage < 1 || result.percentage > 100 ||
                !Number.isSafeInteger(result?.discount_id) || result.discount_id <= 0 ||
                typeof result?.name !== 'string') {
                throw new Error('The discount preview is invalid. Please try again.');
            }
            setDiscount({ ...result, code });
        } catch (error) {
            if (requestId !== discountRequestId.current) return;
            setDiscount(null);
            setDiscountError(error.message);
        } finally {
            if (requestId === discountRequestId.current) setCheckingDiscount(false);
        }
    };

    const handleContactBlur = async () => {
        if (!isReadyForLookup(contactInfo)) return;

        const email = contactInfo.email.trim().toLowerCase();
        const phone = contactInfo.phoneNumber.replace(/\D/g, '');
        const lookupKey = `${email}|${phone}`;
        if (lookupKey === lastLookupKey.current) return;

        lastLookupKey.current = lookupKey;
        const requestId = ++lookupRequestId.current;

        try {
            const result = await lookupCustomer({ email, phone });
            if (requestId !== lookupRequestId.current) return;

            setExistingAnswers(Array.isArray(result?.questionnaire_answers) ? result.questionnaire_answers : []);
        } catch (lookupError) {
            if (requestId !== lookupRequestId.current) return;
            lastLookupKey.current = '';
            setExistingAnswers([]);
        }
    };


    useEffect(() => {
        let active = true;

        getPaymentConfig()
            .then((config) => {
                const amount = Number(config?.deposit_amount);
                if (!Number.isSafeInteger(amount) || amount <= 0) {
                    throw new Error('The deposit configuration is invalid.');
                }
                if (active) setDeposit(amount);
            })
            .catch((error) => {
                if (active) setDepositError(`Unable to load payment details: ${error.message}`);
            });

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        let active = true;

        getQuestionnaire()
            .then((result) => {
                if (!active) return;
                setQuestions(Array.isArray(result) ? result : []);
            })
            .catch((error) => console.error('Failed to load questionnaire:', error));

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        if (contactInfo.phoneNumber === "" || contactInfo.firstName === "" || contactInfo.lastName === "" || contactInfo.email === "") {
            setIsContactInfoComplete(false);
        } else {
            setIsContactInfoComplete(true);
        }
    }, [contactInfo]);

    // Hydrate questionnaire answers from customer lookup
    useEffect(() => {
        if (bookingLocked || questions.length === 0 || existingAnswers.length === 0) return;

        const answersByQuestionId = new Map(
            existingAnswers.map((answer) => [Number(answer.question_id), answer.answer])
        );

        setAnswers((current) => {
            const hydrated = { ...current };
            questions.forEach((question) => {

                const stored = answersByQuestionId.get(Number(question.id));
                if (stored && !touchedAnswers.current[question.id]) {
                    hydrated[question.id] = stored;
                }
            });
            return hydrated;
        });
    }, [questions, existingAnswers, bookingLocked]);

    const handleAnswerChange = (questionId, value) => {
        touchedAnswers.current[questionId] = true;
        setAnswers((current) => ({ ...current, [questionId]: value }));
    };

    const handlePaymentSuccess = (payload) => {
        const routeState = {
            setupIntent: payload.setupIntent,
            verification: payload.verification,
            booking: { ...booking, total_price: discountedPrice, discount },
            amount: payload.verification?.amount,
            currency: payload.verification?.currency,
            status: payload.setupIntent?.status ?? 'succeeded',
            endTime,
            start_time: booking.slotValue,
            deposit: depositDue,
        };

        navigate('/payment-success', { state: routeState });
    };

    const handlePaymentFailure = (payload) => {
        setIntentReady(false);
        setPaymentError(payload.message || 'Payment failed.');

        if (payload.type === 'booking') {
            // Booking failed before payment was attempted; stay on the page.
            return;
        }

        const routeState = {
            error: payload.message || 'Payment failed.',
            code: payload.code,
            type: payload.type,
            booking,
            endTime,
            verification: payload.verification,
        };

        navigate('/payment-failure', { state: routeState });
    };

    const contactFields = [
        { key: 'firstName', label: 'First Name', type: 'text', autoComplete: 'given-name' },
        { key: 'lastName', label: 'Last Name', type: 'text', autoComplete: 'family-name' },
        { key: 'phoneNumber', label: 'Phone Number', type: 'tel', autoComplete: 'tel' },
        { key: 'email', label: 'Email', type: 'email', autoComplete: 'email' },
    ];

    // Deferred-setup flow: the PaymentElement collects card details; the
    // SetupIntent (card on file, no charge) is created on booking submit.
    const stripeOptions = useMemo(() => ({
        mode: 'setup',
        currency: 'usd',
        paymentMethodTypes: ['card'],
        appearance: getStripeAppearance(),
    }), []);

    const createBooking = async () => {
        if (depositDue === null || depositError) {
            throw new Error('Payment details must load before requesting an appointment.');
        }
        if (!agreed) {
            throw new Error('Please accept the payment terms to request your appointment.');
        }
        if (checkingDiscount || (discountCode.trim() && !discount)) {
            throw new Error('Apply or remove your discount code before requesting an appointment.');
        }

        setPaymentError('');
        let appointmentData = bookingAttempt.current;
        if (!appointmentData) {
            appointmentData = buildAppointmentPayload(booking, contactInfo, endTime, questions, answers);
            if (discount) {
                appointmentData.discount_code = discount.code;
                appointmentData.booking_request_id = crypto.randomUUID();
                bookingAttempt.current = appointmentData;
                setBookingLocked(true);
            }
        }
        let response;
        try {
            response = await bookAppointment(appointmentData);
        } catch (error) {
            if (error.status === 400) {
                bookingAttempt.current = null;
                setBookingLocked(false);
            }
            throw error;
        }

        if (!response?.card_verification?.client_secret) {
            throw new Error('No client secret returned from the payment service.');
        }

        setIntentReady(true);
        console.log('Payment verification initialized:', response);
        return response.card_verification;
    };
    

    return (
        <Page 
            title="Checkout"
            subtitle="Review and request your appointment"
        >
            <div className={styles.checkout}>
                <section className={styles.card}>
                    <h2 className={styles.cardTitle}>Contact Info</h2>
                    <div className={styles.fields}>
                        {contactFields.map(({ key, label, type, autoComplete }) => (
                            <label key={key} className={styles.field}>
                                <span className={styles.fieldLabel}>{label}</span>
                                <input
                                    className={styles.input}
                                    type={type}
                                    autoComplete={autoComplete}
                                    value={contactInfo[key]}
                                    disabled={processing || bookingLocked}
                                    onChange={(e) => setContactInfo({ ...contactInfo, [key]: e.target.value })}
                                    onBlur={handleContactBlur}
                                />
                            </label>
                        ))}
                    </div>
                </section>

                {questions.length > 0 && (
                    <section className={styles.card}>
                        <h2 className={styles.cardTitle}>Additional Information</h2>
                        <div className={styles.fields}>
                            {questions.map((question) => (
                                <label key={question.id} className={styles.field}>
                                    <span className={styles.fieldLabel}>{question.question}</span>
                                    <textarea
                                        className={styles.input}
                                        rows={2}
                                        value={answers[question.id] ?? ''}
                                        disabled={processing || bookingLocked}
                                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                    />
                                </label>
                            ))}
                        </div>
                    </section>
                )}

                <section className={styles.card}>
                    <h2 className={styles.cardTitle}>Discount Code</h2>
                    <label className={styles.field} htmlFor="checkout-discount-code">
                        <span className={styles.fieldLabel}>Promo code</span>
                        <div className={styles.discountControls}>
                            <input
                                id="checkout-discount-code"
                                className={styles.input}
                                value={discountCode}
                                maxLength={100}
                                disabled={processing || bookingLocked}
                                onChange={(event) => clearDiscount(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        if (discountCode.trim() && !checkingDiscount && !discount) applyDiscount();
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className={styles.discountButton}
                                disabled={processing || bookingLocked || checkingDiscount || !discountCode.trim() || Boolean(discount)}
                                onClick={applyDiscount}
                            >
                                {checkingDiscount ? 'Checking...' : 'Apply'}
                            </button>
                            {discountCode && (
                                <button
                                    type="button"
                                    className={styles.discountButton}
                                    disabled={processing || bookingLocked}
                                    onClick={() => clearDiscount()}
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                    </label>
                    {discount && <p className={styles.discountHint} role="status">{discount.name}: {discount.percentage}% off applied.</p>}
                    {discountError && <p className={styles.error} role="alert">{discountError}</p>}
                    <p className={styles.discountHint}>Codes are rechecked when you request your appointment.</p>
                </section>

                <section className={styles.card}>
                    <h2 className={styles.cardTitle}>Payment Information</h2>
                    <div className={styles.notice}>
                        <LuLock aria-hidden="true" className={styles.noticeIcon} />
                        <p>
                            <strong>Your card won't be charged today.</strong> It's stored securely with Stripe.
                            {' '}{depositDue === null
                                ? 'Loading deposit details.'
                                : depositDue > 0
                                    ? `A ${formatPrice(depositDue)} deposit is charged once your appointment is confirmed, and the remaining balance after your visit.`
                                    : 'No deposit is required. The full balance is collected after your visit.'}
                        </p>
                    </div>
                    {paymentError && <p className={styles.error} role="alert">{paymentError}</p>}
                    {bookingLocked && !processing && (
                        <p className={styles.discountHint}>Your booking details are saved for a safe retry. Retry this request without changing them.</p>
                    )}
                    {depositError && <p className={styles.error} role="alert">{depositError}</p>}
                    <Elements stripe={stripePromise} options={stripeOptions}>
                        <CardPaymentForm
                            onIntentCreated={createBooking}
                            onSuccess={handlePaymentSuccess}
                            onFailure={handlePaymentFailure}
                            setProcessing={setProcessing}
                            processing={processing}
                        />
                    </Elements>
                </section>
            </div>

            <CheckoutSummary
                booking={booking}
                endTime={endTime}
                price={discountedPrice}
                discount={discount}
                discountAmount={discountAmount}
                duration={duration}
                deposit={depositDue}
                agreed={agreed}
                onAgreeChange={setAgreed}
                formId={checkoutFormId}
                disabled={processing || checkingDiscount || Boolean(discountCode.trim() && !discount) || !isContactInfoComplete || depositDue === null || Boolean(depositError)}
                processing={processing}
            />
            <LoadingScreen open={processing} message="Requesting your appointment…" />
        </Page>
    );
}

export default CheckoutPage;
